import { useState } from 'react';

const AboutCTA = () => {
  const [formState, setFormState] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append('name', formState.name);
      params.append('phone', formState.phone);
      params.append('email', formState.email);
      params.append('message', formState.message);
      await fetch('https://readdy.ai/api/form/d72jsuiaegmdqoca7vpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimate" className="relative bg-pg py-28 overflow-hidden">
      {/* Glow background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-[#00D9FF]/[0.04] rounded-full blur-[100px]" />
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00D9FF]/20 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left: Message block */}
          <div className="scroll-reveal scroll-from-left">
            <span className="inline-block text-[#00D9FF] text-[11px] font-bold tracking-[3px] uppercase mb-5">
              Get Started Today
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-[var(--tp)] leading-tight tracking-tight mb-6">
              Ready to Work with
              <br />
              <span className="text-[#00D9FF]">a Contractor You Can Trust?</span>
            </h2>
            <p className="text-[var(--ts)] text-[16px] leading-relaxed mb-10">
              Whether you need a repair, an upgrade, a new install, or just an honest assessment —
              DOC Electric is ready. We&apos;ll respond quickly, quote fairly, and show up when we say we will.
            </p>

            {/* Trust list */}
            <div className="flex flex-col gap-4">
              {[
                'Free, detailed estimate — no obligation',
                'Clear timeline before work begins',
                'Licensed & insured for every project',
                'Kalamazoo-based, southwest Michigan ready',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center bg-[#00D9FF]/10 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-[#00D9FF] text-xs" />
                  </span>
                  <span className="text-[var(--ts)] text-[14px]">{item}</span>
                </div>
              ))}
            </div>

            {/* Direct phone */}
            <div className="mt-10 pt-10 border-t border-[var(--bd)] flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#00D9FF]/10 rounded-xl flex-shrink-0">
                <i className="ri-phone-fill text-[#00D9FF] text-xl" />
              </div>
              <div>
                <div className="text-[var(--tm)] text-[12px] font-medium uppercase tracking-wider mb-0.5">Call or Text Directly</div>
                <a
                  href="tel:+12693700765"
                  className="text-[var(--tp)] font-black text-2xl hover:text-[#00D9FF] transition-colors duration-200 cursor-pointer"
                >
                  (269) 370-0765
                </a>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-dp border border-[var(--bd)] rounded-2xl p-8 md:p-10 scroll-reveal scroll-from-right">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-14 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-[#00D9FF]/10 rounded-full mb-5">
                  <i className="ri-checkbox-circle-fill text-[#00D9FF] text-3xl" />
                </div>
                <h3 className="text-[var(--tp)] font-black text-xl mb-2">Request Sent!</h3>
                <p className="text-[var(--tm)] text-[14px] leading-relaxed max-w-xs">
                  Thank you — we&apos;ll reach out to schedule your free estimate shortly.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="about-cta-form"
                className="flex flex-col gap-5"
              >
                <div>
                  <div className="text-[var(--tp)] font-black text-xl mb-1">Request a Free Estimate</div>
                  <div className="text-[var(--tm)] text-[13px]">We&apos;ll respond within one business day.</div>
                </div>

                <div>
                  <label className="block text-[var(--tm)] text-[12px] font-medium uppercase tracking-wider mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formState.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="w-full bg-[var(--inp-bg)] border border-[var(--inp-border)] rounded-lg px-4 py-3 text-[var(--inp-text)] text-sm focus:outline-none focus:border-[#00D9FF]/50 transition-all duration-200"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[var(--tm)] text-[12px] font-medium uppercase tracking-wider mb-1.5">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formState.phone}
                      onChange={handleChange}
                      placeholder="(269) 000-0000"
                      className="w-full bg-[var(--inp-bg)] border border-[var(--inp-border)] rounded-lg px-4 py-3 text-[var(--inp-text)] text-sm focus:outline-none focus:border-[#00D9FF]/50 transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-[var(--tm)] text-[12px] font-medium uppercase tracking-wider mb-1.5">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formState.email}
                      onChange={handleChange}
                      placeholder="you@email.com"
                      className="w-full bg-[var(--inp-bg)] border border-[var(--inp-border)] rounded-lg px-4 py-3 text-[var(--inp-text)] text-sm focus:outline-none focus:border-[#00D9FF]/50 transition-all duration-200"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[var(--tm)] text-[12px] font-medium uppercase tracking-wider mb-1.5">Tell Us About Your Project</label>
                  <textarea
                    name="message"
                    value={formState.message}
                    onChange={handleChange}
                    placeholder="Describe what you need — repairs, installation, upgrade..."
                    rows={4}
                    maxLength={500}
                    className="w-full bg-[var(--inp-bg)] border border-[var(--inp-border)] rounded-lg px-4 py-3 text-[var(--inp-text)] text-sm focus:outline-none focus:border-[#00D9FF]/50 transition-all duration-200 resize-none"
                  />
                  <div className="text-right text-[var(--tm)] text-[11px] mt-1">{formState.message.length}/500</div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-[#00D9FF] hover:bg-[#00c5e8] disabled:opacity-60 text-[#0A1628] font-black text-[13px] uppercase tracking-wider rounded-lg transition-all duration-300 hover:shadow-[0_0_28px_rgba(0,217,255,0.4)] hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
                >
                  {submitting ? 'Sending...' : 'Send Estimate Request'}
                </button>

                <p className="text-[var(--tm)] text-[11px] text-center">
                  Licensed &amp; Insured · Kalamazoo, MI · No pressure, no obligation
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutCTA;
